import type { XpTransactionList, XpTransactionListParams } from 'src/types/xp';

import { useInfiniteQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { XP_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

/** GET /xp/transactions — the account wallet's ledger, like the balance (see useXpBalance); its screen gates on the wallet. */
export function useXpTransactions(params?: XpTransactionListParams, options?: { enabled?: boolean }) {
  const session = useRequestSession();

  return useInfiniteQuery({
    enabled: options?.enabled ?? true,
    queryKey: QueryKeys.forSession(QueryKeys.xpTransactions(params), session),
    staleTime: XP_STALE_TIME,
    queryFn: async ({ pageParam = 0 }): Promise<XpTransactionList> => {
      const response = await axios.get<XpTransactionList>('/xp/transactions', {
        params: {
          ...params,
          offset: pageParam,
        },
      });
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { offset, limit, total } = lastPage;
      const nextOffset = offset + limit;
      return nextOffset < total ? nextOffset : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      const { offset, limit } = firstPage;
      const prevOffset = offset - limit;
      return prevOffset >= 0 ? prevOffset : undefined;
    },
  });
}
